/**
 * ч.1412: підпис поля стає його іменем.
 *
 * Більшість форм написано `<label>Організація</label><select … />` — підпис стоїть поруч, але з полем
 * не повʼязаний. На вигляд усе добре, а в дереві доступності поле безіменне: диктор екрана не каже, що
 * це за поле, а агент відео-інструкцій не може його вказати і ходив формою через Tab, поки рамка
 * лишалась на минулому полі. Таких підписів понад тисячу у 196 файлах, тому імʼя ставить один механізм
 * на всі сторінки (те саме для рядків таблиць робить `labelCellFields`), вигляд і поведінка не змінюються.
 *
 * Поле з власним іменем не чіпаємо: `aria-label`, `aria-labelledby`, `htmlFor`, обгортка `<label>`.
 *
 * Фільтр у списку часто взагалі без підпису: `<select><option>Усі організації</option>…`. Його імʼя — перший
 * пункт: так його описує сама сторінка, і агент вказує саме цей фільтр, а не перший список на екрані.
 */
const CONTROL = 'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="button"])'
  + ':not([type="submit"]),select,textarea';
const SCAN = `label,${CONTROL}`;

type Pending = { label: HTMLLabelElement; scope: Element; text: string };

function bareLabel(label: HTMLLabelElement): Pending | null {
  if (label.htmlFor || label.control || label.querySelector(CONTROL)) return null;
  const text = (label.textContent ?? '').replace(/\s+/g, ' ').trim();
  const scope = label.closest('.field') ?? label.parentElement;
  return text && scope ? { label, scope, text } : null;
}

type Control = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function setName(c: Control, text: string): void {
  const ours = c.dataset.fieldName === '1';
  if (!ours && (c.hasAttribute('aria-label') || c.hasAttribute('aria-labelledby') || c.labels?.length)) return;
  if (c.getAttribute('aria-label') !== text) c.setAttribute('aria-label', text);
  c.dataset.fieldName = '1';
}

function assign(c: Control, { label, scope, text }: Pending): void {
  if (!scope.contains(c)) return;
  // Таблиця всередині блоку з підписом: її поля іменує `labelCellFields`, підпис блоку — не про них.
  const table = c.closest('table');
  if (table && !table.contains(label)) return;
  setName(c, text);
}

/** Список без підпису: шапка називає свої поля сама, рядки таблиці — `labelCellFields`. */
function nameByFirstOption(root: ParentNode, named: Set<Element>): void {
  root.querySelectorAll<HTMLSelectElement>('select').forEach((s) => {
    if (named.has(s) || s.closest('table, .app__header')) return;
    const first = (s.options[0]?.text ?? '').replace(/\s+/g, ' ').trim();
    if (first) setName(s, first);
  });
}

/** Один прохід у порядку документа: підпис іменує перше поле після себе у своєму блоці. */
export function nameFields(root: ParentNode = document): void {
  let pending: Pending | null = null;
  const named = new Set<Element>();
  root.querySelectorAll<HTMLElement>(SCAN).forEach((el) => {
    if (el.tagName === 'LABEL') {
      pending = bareLabel(el as HTMLLabelElement);
    } else if (pending) {
      assign(el as Control, pending);
      if ((el as Control).dataset.fieldName === '1') named.add(el);
      pending = null;
    }
  });
  nameByFirstOption(root, named);
}

const THROTTLE_MS = 120;

/** Сторінка малюється частинами, поля зʼявляються за умовами: перегляд після кожної зміни DOM. */
export function installFieldNames(): void {
  if (typeof MutationObserver === 'undefined' || !document.body) return;
  let timer = 0;
  const run = () => { timer = 0; nameFields(document.body); };
  new MutationObserver(() => { if (!timer) timer = window.setTimeout(run, THROTTLE_MS); })
    .observe(document.body, { childList: true, subtree: true, characterData: true });
  run();
}
